import React, { Fragment } from 'react'
import PropTypes from 'prop-types'

const propTypes = {
    enable: PropTypes.bool
}

const defaultProps = {
    enable: false
}

/**
 * A non-visual element to conditionally hide or show its children. This element is meant to be used within the JSX
 * tree for better readability when conditional rendering is necessary. Instead of using embedded expressions as
 * inline JS code with logical operators in curly brackets, this element will not break the JSX syntax structure.
 * The children elements will be rendered (wrapped in a `React.Fragment`) when the given `enable` property is true.
 * Otherwise `null` is returned and nothing will be rendered.
 *
 * BE AWARE: Due to JavaScript being an eagerly evaluated language child-components will be evaluated even if enable is false.
 * If you need to delay the evaluation due to performance or null-pointer issues, you can use a function as child.
 *
 * @see {@link https://reactjs.org/docs/introducing-jsx.html#embedding-expressions-in-jsx}
 * @see {@link https://reactjs.org/docs/conditional-rendering.html}
 *
 * @example
 *
 * <Conditional enable={booleanFlag}>
 *     <div>Some simple jsx</div>
 * </Conditional>
 * <Conditional enable={booleanFlag}>
 *     {() => <MyComplexComponent {...props} />}
 * </Conditional>
 *
 * @param {Object} props
 * @param {React.ReactNode|function} props.children - The elements or a function returning the elements that conditionally shall be rendered.
 * @param {boolean} props.enable - The condition when to render the children.
 * @returns {React.ReactNode} The `React.Fragment` wrapped children or `null` depending on `enable` condition.
 */
const Conditional = props => {
    const { children, enable } = props

    if (!enable) {
        return null
    }

    if (typeof children === 'function') {
        return <Fragment>{children()}</Fragment>
    }

    return <Fragment>{children}</Fragment>
}

Conditional.propTypes = propTypes
Conditional.defaultProps = defaultProps

export { Conditional }
